// src/screens/PlanEmergencyFund.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 46B Section 6 — Savings lever: full redesign.
// Parts A–E: Current position, EF target, Other savings goals, Trajectory chart.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  ReferenceLine, ResponsiveContainer, Tooltip, Legend,
} from 'recharts';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { useCommaInput } from '../utils/useCommaInput';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

export default function PlanEmergencyFund({ data, onUpdate }: Props) {
  // ── Part A: Calculated values ─────────────────────────────────────────────
  const essentialsMonthly = (data.essentialsUtilities || 0) + (data.essentialsFood || 0) + (data.essentialsTravel || 0);
  const calculatedOutgoings = essentialsMonthly + (data.mortgagePayment || 0);

  // Adjustable outgoings basis (Part B)
  const [adjustedOutgoings, setAdjustedOutgoings] = useState(
    data.planEfAdjustedOutgoings || calculatedOutgoings
  );
  const monthlyOutgoings = adjustedOutgoings > 0 ? adjustedOutgoings : calculatedOutgoings;

  const adjustedOutgoingsInput = useCommaInput(adjustedOutgoings, (v) => {
    setAdjustedOutgoings(v);
    onUpdate({ planEfAdjustedOutgoings: v });
  });

  // Total savings
  const savingsTotal = (data.savingsAccounts || []).reduce((sum, a) => sum + (a.balance || 0), 0);
  const efTotal = (data.efAccount1Balance || 0) + (data.efAccount2Balance || 0);
  const totalSavings = savingsTotal + efTotal;

  // Current monthly savings
  const currentMonthlySavings = (data.efTopUp || 0) + (data.sinkingFundsTotal || 0);

  // ── Part B: EF target selection ───────────────────────────────────────────
  const [selectedTargetMonths, setSelectedTargetMonths] = useState<3 | 6 | 'custom'>(
    data.planEfTargetMonths === 3 ? 3 : data.planEfTargetMonths === 6 ? 6 : 'custom'
  );
  const [customTargetMonths, setCustomTargetMonths] = useState(data.planEfTargetMonths || 6);
  const customTargetInput = useCommaInput(customTargetMonths, (v) => {
    setCustomTargetMonths(v);
    if (selectedTargetMonths === 'custom') onUpdate({ planEfTargetMonths: v });
  });

  const effectiveTargetMonths = selectedTargetMonths === 'custom' ? customTargetMonths : selectedTargetMonths;
  const target3m = monthlyOutgoings * 3;
  const target6m = monthlyOutgoings * 6;
  const efTarget = monthlyOutgoings * effectiveTargetMonths;
  const coverageMonths = monthlyOutgoings > 0 ? totalSavings / monthlyOutgoings : 0;

  const handleTargetMonthsChange = (newTarget: 3 | 6 | 'custom') => {
    setSelectedTargetMonths(newTarget);
    const effective = newTarget === 'custom' ? customTargetMonths : newTarget;
    onUpdate({ planEfTargetMonths: effective });
  };

  // ── Part C: Other savings goals ───────────────────────────────────────────
  const [savingsGoals, setSavingsGoals] = useState<Array<{ name: string; amount: number; timelineMonths: number }>>(
    data.planSavingsGoals?.length > 0 ? data.planSavingsGoals : []
  );

  const goalsTotal = savingsGoals.reduce((sum, g) => sum + (g.amount || 0), 0);
  const availableForEF = Math.max(0, totalSavings - goalsTotal);
  const efSurplusShortfall = availableForEF - efTarget;

  const addGoal = () => {
    if (savingsGoals.length >= 3) return;
    const updated = [...savingsGoals, { name: '', amount: 0, timelineMonths: 12 }];
    setSavingsGoals(updated);
    onUpdate({ planSavingsGoals: updated });
  };

  const updateGoal = (index: number, field: string, value: string | number) => {
    const updated = savingsGoals.map((g, i) => i === index ? { ...g, [field]: value } : g);
    setSavingsGoals(updated);
    onUpdate({ planSavingsGoals: updated });
  };

  const removeGoal = (index: number) => {
    const updated = savingsGoals.filter((_, i) => i !== index);
    setSavingsGoals(updated);
    onUpdate({ planSavingsGoals: updated });
  };

  // ── Part E: Savings trajectory chart ──────────────────────────────────────
  const chartData = useMemo(() => {
    if (currentMonthlySavings <= 0 && totalSavings <= 0) return [];
    const maxMonths = 36;
    const points: Array<{ month: number; balance: number }> = [];
    for (let m = 0; m <= maxMonths; m++) {
      points.push({
        month: m,
        balance: Math.round(totalSavings + (currentMonthlySavings * m)),
      });
    }
    return points;
  }, [totalSavings, currentMonthlySavings]);

  // Find month when EF target is met
  const efFundedMonth = useMemo(() => {
    if (totalSavings >= efTarget) return 0;
    if (currentMonthlySavings <= 0) return null;
    return Math.ceil((efTarget - totalSavings) / currentMonthlySavings);
  }, [totalSavings, efTarget, currentMonthlySavings]);

  // ── Top-up for shortfall ──────────────────────────────────────────────────
  const [monthlyTopUp, setMonthlyTopUp] = useState(data.planEfMonthlyTopUp || 0);
  const topUpInput = useCommaInput(monthlyTopUp, (v) => {
    setMonthlyTopUp(v);
    onUpdate({ planEfMonthlyTopUp: v });
  });

  const shortfall = Math.max(0, efTarget - totalSavings);
  const isFullyFunded = shortfall <= 0;

  // ── Redirect ──────────────────────────────────────────────────────────────
  const [redirectTo, setRedirectTo] = useState<'isa' | 'pension' | 'mortgage' | 'savings'>(
    data.planEfRedirectTo || 'isa'
  );
  const handleRedirectChange = (r: 'isa' | 'pension' | 'mortgage' | 'savings') => {
    setRedirectTo(r);
    onUpdate({ planEfRedirectTo: r });
  };

  return (
    <div className="min-h-screen bg-white p-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-ma-navy mb-2">Savings</h1>
        <p className="text-lg text-gray-600 mb-6">
          Your savings buffer — how much you need, and where it goes next.
        </p>

        {/* ── Part A — Current Position (read-only) ────────────────────────── */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 mb-6">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Current Position</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Total savings balance</div>
              <div className="text-lg font-bold text-ma-navy">{fmt(totalSavings)}</div>
            </div>
            <div>
              <div className="text-gray-500">Current monthly savings</div>
              <div className="text-lg font-bold text-ma-navy">{fmt(currentMonthlySavings)}/mo</div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
            <span className="font-medium">EF basis:</span> Essentials {fmt(essentialsMonthly)} + Mortgage {fmt(data.mortgagePayment || 0)} ={' '}
            <span className="font-bold">{fmt(calculatedOutgoings)}/month</span>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            EF target (3 months): <span className="font-semibold">{fmt(target3m)}</span>{' | '}
            EF target (6 months): <span className="font-semibold">{fmt(target6m)}</span>
          </div>
          <div className="mt-2 text-sm">
            <span className="text-gray-500">Current coverage:</span>{' '}
            <span className="font-bold text-ma-navy">{coverageMonths.toFixed(1)} months</span>
          </div>
        </div>

        {/* ── Part B — EF Target Selection ─────────────────────────────────── */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-ma-navy mb-4">Choose Your Target</h2>

          <div className="flex gap-3 flex-wrap mb-4">
            <button
              onClick={() => handleTargetMonthsChange(3)}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                selectedTargetMonths === 3 ? 'bg-ma-navy text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="font-semibold">3 months</div>
              <div className="text-sm opacity-80">{fmt(target3m)}</div>
            </button>
            <button
              onClick={() => handleTargetMonthsChange(6)}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                selectedTargetMonths === 6 ? 'bg-ma-navy text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="font-semibold">6 months</div>
              <div className="text-sm opacity-80">{fmt(target6m)}</div>
            </button>
            <div className={`px-6 py-3 rounded-full border-2 transition-all flex items-center gap-2 ${
              selectedTargetMonths === 'custom' ? 'border-ma-navy bg-blue-50' : 'border-gray-200 bg-white'
            }`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={selectedTargetMonths === 'custom'} onChange={() => handleTargetMonthsChange('custom')} className="w-4 h-4" />
                <span className="font-medium text-gray-700">Custom:</span>
                <input {...customTargetInput} placeholder="0" className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm" />
                <span className="text-gray-600">months</span>
              </label>
            </div>
          </div>

          {/* Directors note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-gray-600 mb-4">
            <span className="font-semibold">Directors:</span> best practice is to hold 3 months of your EF target in company reserves.
            Your total 6-month target is {fmt(target6m)} — consider holding {fmt(Math.round(target6m / 2))} personally
            and {fmt(Math.round(target6m / 2))} in company reserves.
          </div>

          {/* Adjustable outgoings basis */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
              Adjust monthly outgoings basis
            </label>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">£</span>
              <input
                {...adjustedOutgoingsInput}
                className="w-40 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white"
                placeholder={String(calculatedOutgoings)}
              />
              <span className="text-xs text-gray-400">/month (defaults to essentials + mortgage)</span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Current position</span>
              <span className="text-sm font-medium text-gray-700">{coverageMonths.toFixed(1)} months</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              {isFullyFunded ? (
                <div className="h-full flex">
                  <div className="h-full bg-green-500" style={{ width: `${Math.min(100, (effectiveTargetMonths / coverageMonths) * 100)}%` }} />
                  <div className="h-full bg-green-300" style={{ width: `${Math.min(100, ((coverageMonths - effectiveTargetMonths) / coverageMonths) * 100)}%` }} />
                </div>
              ) : (
                <div className="h-3 rounded-full bg-red-400 transition-all"
                  style={{ width: `${Math.min(100, (coverageMonths / effectiveTargetMonths) * 100)}%` }} />
              )}
            </div>
            <div className="flex justify-between mt-1 text-xs text-gray-400">
              <span>Current: {coverageMonths.toFixed(1)} months</span>
              <span>Target: {effectiveTargetMonths} months</span>
            </div>
          </div>

          {/* Shortfall section */}
          {!isFullyFunded && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-semibold text-amber-900 mb-3">
                {fmt(shortfall)} to go to reach your {effectiveTargetMonths}-month target.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-sm text-gray-700">Monthly top-up:</label>
                <span className="text-gray-400">£</span>
                <input
                  {...topUpInput}
                  placeholder="0"
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm text-right"
                />
                <span className="text-sm text-gray-400">/month</span>
              </div>
              {monthlyTopUp > 0 && (
                <p className="text-sm text-gray-600">
                  At {fmt(monthlyTopUp)}/month you'll complete in{' '}
                  <span className="font-semibold text-ma-navy">{Math.ceil(shortfall / monthlyTopUp)} months</span>
                </p>
              )}
            </div>
          )}

          {/* Fully funded — redirect */}
          {isFullyFunded && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3 mb-4">
                <span className="text-xl">✓</span>
                <div>
                  <h3 className="font-semibold text-green-900">Emergency fund fully funded</h3>
                  <p className="text-sm text-green-700">{fmt(totalSavings)} covers {coverageMonths.toFixed(1)} months.</p>
                </div>
              </div>
              {currentMonthlySavings > 0 && (
                <div className="pt-3 border-t border-green-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Where should {fmt(currentMonthlySavings)}/month redirect?
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {(['isa', 'mortgage', 'pension', 'savings'] as const).map((opt) => (
                      <button key={opt} onClick={() => handleRedirectChange(opt)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                          redirectTo === opt ? 'bg-ma-navy text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}>
                        {opt === 'isa' ? 'ISA' : opt === 'mortgage' ? 'Mortgage' : opt === 'pension' ? 'Pension' : 'Keep in savings'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Part C — Other Savings Goals ─────────────────────────────────── */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-ma-navy mb-4">Other Savings Goals</h2>
          <p className="text-sm text-gray-600 mb-4">What else is this savings pot earmarked for?</p>

          {savingsGoals.map((goal, i) => (
            <div key={i} className="flex items-center gap-3 mb-3 bg-gray-50 rounded-lg p-3">
              <input
                type="text" value={goal.name} onChange={e => updateGoal(i, 'name', e.target.value)}
                placeholder="Goal name" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <div className="flex items-center gap-1">
                <span className="text-gray-400 text-sm">£</span>
                <input
                  type="number" value={goal.amount || ''} onChange={e => updateGoal(i, 'amount', Number(e.target.value))}
                  placeholder="0" className="w-24 px-2 py-2 border border-gray-200 rounded-lg text-sm text-right"
                />
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number" value={goal.timelineMonths || ''} onChange={e => updateGoal(i, 'timelineMonths', Number(e.target.value))}
                  placeholder="12" className="w-16 px-2 py-2 border border-gray-200 rounded-lg text-sm text-right"
                />
                <span className="text-xs text-gray-400">mo</span>
              </div>
              <button onClick={() => removeGoal(i)} className="text-red-400 hover:text-red-600 text-sm px-2">×</button>
            </div>
          ))}

          {savingsGoals.length < 3 && (
            <button onClick={addGoal}
              className="text-sm text-ma-navy font-medium hover:underline">
              + Add goal
            </button>
          )}

          {savingsGoals.length > 0 && (
            <div className="mt-4 bg-gray-50 rounded-lg p-4 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Earmarked for goals</span>
                <span className="font-medium">{fmt(goalsTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Available for EF (above goals)</span>
                <span className="font-medium">{fmt(availableForEF)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t border-gray-200 pt-1">
                <span>{efSurplusShortfall >= 0 ? 'EF surplus' : 'EF shortfall'}</span>
                <span className={efSurplusShortfall >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {fmt(Math.abs(efSurplusShortfall))}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ── Part E — Savings Trajectory Chart ────────────────────────────── */}
        {chartData.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
            <h3 className="text-sm font-semibold text-ma-navy uppercase tracking-wide mb-4">Savings Trajectory</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }}
                  label={{ value: 'Months', position: 'insideBottom', offset: -10, fontSize: 11 }} />
                <YAxis tickFormatter={(v: number) => `£${Math.round(v / 1000)}k`}
                  tick={{ fill: '#6b7280', fontSize: 11 }} />
                <Tooltip formatter={(v: any) => fmt(Number(v))} />
                <Legend verticalAlign="top" />
                <Line type="monotone" dataKey="balance" stroke="#1E3A5F" strokeWidth={2} dot={false} name="Savings balance" />
                {/* EF target reference line */}
                <ReferenceLine y={efTarget} stroke="#C9A84C" strokeDasharray="8 4" strokeWidth={2}
                  label={{ value: `EF target (${effectiveTargetMonths}mo)`, position: 'insideTopRight', fill: '#C9A84C', fontSize: 11 }} />
              </LineChart>
            </ResponsiveContainer>
            {efFundedMonth !== null && efFundedMonth > 0 && (
              <p className="text-xs text-green-600 mt-2">
                EF funded ✓ at month {efFundedMonth} ({Math.round(efFundedMonth / 12 * 10) / 10} years)
              </p>
            )}
            {efFundedMonth === 0 && (
              <p className="text-xs text-green-600 mt-2">EF funded ✓ — already above target</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
